/**
 * Wurzelkomponente des DUELINE-Clients.
 *
 * Sie haelt vier Dinge zusammen:
 * 1. die Verbindung zum Server,
 * 2. die Eingaben des Spielers (Bewegung, Skills, Zielauswahl),
 * 3. das 3D-Bild (PlayCanvas),
 * 4. die Bedienoberflaeche (React).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { getCreep, getHero, getSkillInSlot, type SkillDefinition } from '@dueline/game-data';
import { getHeroRangeFactor, withHeroRange } from '@dueline/game-rules';
import type { DevActionTypeValue, PlayerSide } from '@dueline/shared';
/*
 * NUR der Typ (13.10.2026): Die Datei selbst wird erst beim Matchstart
 * nachgeladen - siehe baueWelt. Ein normaler Import zoege PlayCanvas ins
 * Hauptbuendel, und das Menue muesste knapp vier Megabyte auswerten, bevor
 * es ueberhaupt erscheint.
 */
import type { GameView } from './game/GameView.js';
import { InputController } from './input/InputController.js';
import { installiereZweitenFinger } from './input/secondFinger.js';
import { shouldHandleShortcut } from './input/keyboard.js';
import {
  SkillInput,
  resolveAimTarget,
  resolveStickTarget,
  tasteGehalten,
} from './input/SkillInput.js';
import {
  MausBefehl,
  SKILL_TASTEN,
  ladeSteuerung,
  speichereSteuerung,
  type Steuerung,
} from './input/mausSteuerung.js';
import { buildAimPreview, needsAim } from './game/skillPreview.js';

/**
 * Der Skill mit der Reichweite dieses Heroes - oder undefined, wenn es ihn
 * nicht gibt. Die Rechnung steht in game-rules, damit Server und Anzeige
 * garantiert dieselbe Reichweite benutzen.
 */
function mitReichweite(definition: SkillDefinition | undefined, talentId: string) {
  return definition ? withHeroRange(definition, talentId) : undefined;
}
import {
  MatchConnection,
  eingabeAusSpielrichtung,
  resolveServerUrl,
  type JoinMode,
  type MatchSnapshot,
} from './net/MatchConnection.js';
import { ConnectionOverlay, PauseNotice } from './ui/ConnectionOverlay.js';
import { DebugPanel } from './ui/DebugPanel.js';
import {
  enterFullscreen,
  isFullscreen,
  isTouchDevice,
  vollbildBeimErstenTipp,
  vollbildMoeglich,
} from './ui/fullscreen.js';
import { StartbildschirmHinweis } from './ui/StartbildschirmHinweis.js';
import { HudShell } from './ui/HudShell.js';
import type { ManagementSection } from './ui/ManagementNav.js';
import { ArmyComposer } from './ui/ArmyComposer.js';
import { SkillTreePanel } from './ui/SkillTreePanel.js';
import { ItemShop } from './ui/ItemShop.js';
import { PotionBar } from './ui/PotionBar.js';
import { Sound } from './audio/Sound.js';
import { WatchFrontlineBanner } from './ui/WatchFrontlineBanner.js';
import {
  addCreep,
  canAddCreep,
  copyDraft,
  emptyDraft,
  isSameDraft,
  removeCreep,
  type WaveDraft,
} from './ui/composerModel.js';
import { MatchEndOverlay } from './ui/MatchEndOverlay.js';
import { Lobby } from './ui/Lobby.js';
import { MainMenu } from './ui/MainMenu.js';
import { Zugangstor } from './ui/Zugangstor.js';
import { frageZugang } from './net/zugangstor.js';
import { ladeZugangscode } from './net/zugangscode.js';
import { OrientationGate } from './ui/OrientationGate.js';
import { brauchtSpielwelt, weltSichtbar, zeigeVerbindungsmeldung } from './ui/weltBedarf.js';
import { matchToene, type TonGedaechtnis } from './ui/matchToene.js';
import { SkillBar } from './ui/SkillBar.js';
import { VirtualJoystick } from './ui/VirtualJoystick.js';

/**
 * Baut die Spielwelt auf und gibt zurueck, wie man sie wieder abraeumt.
 *
 * Eine eigene Funktion ausserhalb der Komponente (13.10.2026): Der Effekt
 * darueber soll auf einen Blick zeigen, WANN geladen wird - nicht, was dabei
 * alles entsteht.
 */
function baueWelt(
  GameViewKlasse: typeof import('./game/GameView.js').GameView,
  stage: HTMLDivElement,
  connection: MatchConnection,
  skills: SkillInput,
  viewRef: { current: GameView | null },
): () => void {
  const view = new GameViewKlasse(
    stage,
    {
      getState: () => connection.getState(),
      getOwnSessionId: () => connection.getOwnSessionId(),
      getOpponentSessionId: () => connection.getOpponentSessionId(),
      tickPrediction: (nowMs) => connection.tickPrediction(nowMs),
      getRenderPosition: (player) => connection.getRenderPosition(player),
    },
    (timing) => connection.reportFrameTiming(timing.fps, timing.frameTimeMs),
  );
  viewRef.current = view;

  /**
   * Rechnet den laufenden Zielvorgang in einen Punkt auf dem Boden um und
   * zeigt ihn an. Laeuft in jedem Bild, damit der Ring dem Finger folgt.
   */
  const zeigeZielpunkt = () => {
    const zielen = skills.getAiming();
    const bereit = skills.getArmed();
    const player = connection.getOwnPlayer();

    if ((!zielen && !bereit) || !player) {
      view.setAimIndicator(null);
      return;
    }

    const slot = zielen ? zielen.slot : bereit!.slot;
    // Mit der Reichweite, die fuer DIESEN Hero gilt: Das Talent "Eagle Eye"
    // des Deadeye schiesst weiter, und die Vorschau muss das zeigen.
    const definition = mitReichweite(getSkillInSlot(player.heroId, slot), player.talentId);
    if (!definition) {
      view.setAimIndicator(null);
      return;
    }

    /*
     * Drei Wege zum Zielpunkt:
     * - Zielstick (Finger): die Auslenkung gegenueber dem Startpunkt,
     * - Ziehen mit der Maus: der Punkt unter dem Zeiger,
     * - bereitliegender Skill: die letzte Mausposition, sonst Blickrichtung.
     */
    let boden: { x: number; y: number } | null = null;

    if (zielen?.stick) {
      boden = resolveStickTarget(
        { x: player.x, y: player.y },
        player.facing,
        zielen.currentX - zielen.originX,
        zielen.currentY - zielen.originY,
        Math.max(definition.castRange, definition.dashDistance),
        player.side as PlayerSide,
      );
    } else {
      const zeiger = zielen
        ? { x: zielen.inDeadZone ? null : zielen.currentX, y: zielen.currentY }
        : { x: bereit!.pointerX, y: bereit!.pointerY };
      boden =
        zeiger.x === null || zeiger.y === null ? null : view.screenToGround(zeiger.x, zeiger.y);
    }

    /*
     * Gezeigt wird beides: die groesste Reichweite als Ring um den Hero und
     * die Flaeche, die der Skill trifft (Rueckmeldung vom 24.09.2026).
     * Selbstbuffs haben beides nicht - dort kommt null zurueck.
     */
    view.setAimIndicator(
      buildAimPreview(definition, { x: player.x, y: player.y }, boden, player.facing),
    );
  };

  const unsubscribeAiming = skills.subscribe(zeigeZielpunkt);

  return () => {
    unsubscribeAiming();
    view.destroy();
    viewRef.current = null;
  };
}

export function App() {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const connectionRef = useRef<MatchConnection | null>(null);
  const viewRef = useRef<GameView | null>(null);

  /*
   * Steht die Infoseite zum geschlossenen Playtest davor?
   * (Produktentscheidung vom 04.10.2026)
   *
   * "frage" = der Server wurde noch nicht gehoert. Solange wird gar nichts
   * aufgebaut - sonst blitzte das Menue kurz auf, bevor die Infoseite es
   * wieder verdeckt.
   */
  const [tor, setTor] = useState<'frage' | 'zu' | 'offen'>('frage');

  useEffect(() => {
    const abbruch = new AbortController();

    void frageZugang(ladeZugangscode(), abbruch.signal).then((antwort) => {
      if (abbruch.signal.aborted) return;
      /*
       * Keine Antwort (Server nicht erreichbar) laesst durch: Die Tuer
       * steht unveraendert auf dem Server in onAuth. Eine Codeabfrage, die
       * gerade niemand beantworten kann, haelt nur den Spieler auf.
       */
      if (antwort === null) return setTor('offen');
      setTor(antwort.geschlossen && !antwort.erlaubt ? 'zu' : 'offen');
    });

    return () => abbruch.abort();
  }, []);

  const [snapshot, setSnapshot] = useState<MatchSnapshot | null>(null);
  /**
   * Derselbe Stand als Referenz.
   *
   * Rueckruffunktionen wie addToDraft werden einmal angelegt und muessen
   * trotzdem den AKTUELLEN Goldstand sehen - ueber den Zustand allein saehen
   * sie immer den von ihrer Entstehung.
   */
  const snapshotRef = useRef<MatchSnapshot | null>(null);
  const [input, setInput] = useState<InputController | null>(null);
  const [skillInput, setSkillInput] = useState<SkillInput | null>(null);
  /*
   * Die gewaehlte Steuerung (Produktentscheidung vom 25.10.2026).
   *
   * Sie liegt HIER und nicht im Menue: Im Match gilt dieselbe Einstellung,
   * und gemerkt wird sie nur an einer Stelle.
   */
  const [steuerung, setSteuerung] = useState<Steuerung>(() => ladeSteuerung());

  /**
   * Der laufende Klickbefehl der Maussteuerung (25.10.2026).
   *
   * Im Zustand und nicht in einem Ref: Es gibt ihn genau einmal, und sein
   * Inhalt laesst React nie neu zeichnen - gefragt wird er im Takt der
   * Verbindung, zwanzig mal je Sekunde. Veraenderlich ist der INHALT des
   * Objekts, nicht der Zustand selbst.
   */
  const [mausBefehl] = useState(() => new MausBefehl());

  /*
   * In der Maussteuerung sind die Lauftasten aus - Skill 2 liegt dort auf
   * dem W (siehe mausSteuerung.ts). Und ein Wechsel hebt den laufenden
   * Befehl auf: Sonst laeuft der Hero nach dem Umschalten noch zu einem
   * Punkt, den man in der anderen Steuerung angeklickt hat.
   */
  useEffect(() => {
    input?.setTastenErlaubt(steuerung !== 'maus');
    mausBefehl.loesche();
  }, [input, steuerung, mausBefehl]);

  const waehleSteuerung = useCallback((gewaehlt: Steuerung) => {
    setSteuerung(gewaehlt);
    speichereSteuerung(gewaehlt);
  }, []);

  /*
   * Kein Browsermenue, solange mit der Maus gelaufen wird (Robin,
   * 25.10.2026: "es oeffnet sich immer wieder das Browser-Rechtsklickmenue").
   *
   * Am FENSTER und nicht an der Spielflaeche: Ueber ihr liegt das halbe
   * HUD, und ein Rechtsklick dort kam nie bei ihr an. Nur in dieser
   * Steuerung - sonst nimmt man dem Spieler im Menue das gewohnte Menue weg
   * (Matchcode kopieren).
   */
  useEffect(() => {
    if (steuerung !== 'maus') return;
    const unterdruecke = (event: MouseEvent) => event.preventDefault();
    window.addEventListener('contextmenu', unterdruecke);
    return () => window.removeEventListener('contextmenu', unterdruecke);
  }, [steuerung]);

  /*
   * Dieselbe Eingabe noch einmal als Verweis: Rueckruffunktionen wie der Klick
   * auf die Spielflaeche werden einmal angelegt und saehen ueber den Zustand
   * allein fuer immer "noch nichts da".
   */
  const skillInputRef = useRef<SkillInput | null>(null);

  // --- Army Composer (Abschnitt 70) ---
  // Die geplante Welle bleibt erhalten, auch wenn der Composer geschlossen wird.
  const [draft, setDraft] = useState<WaveDraft>(emptyDraft);
  /*
   * Es ist immer HOECHSTENS EIN Management-Fenster offen (Rueckmeldung vom
   * 26.09.2026): Wer rechts etwas anderes anklickt, wechselt dorthin; wer
   * dasselbe noch einmal anklickt, schliesst es. Vorher konnten sich Composer,
   * Shop und Defense uebereinanderlegen.
   */
  const [activeSection, setActiveSection] = useState<ManagementSection>(null);

  const composerOpen = activeSection === 'army';
  const skillTreeOpen = activeSection === 'talent';
  const shopOpen = activeSection === 'shop';

  /** Oeffnet einen Bereich - oder schliesst ihn, wenn er schon offen ist. */
  const toggleSection = useCallback((section: Exclude<ManagementSection, null>) => {
    setActiveSection((offen) => (offen === section ? null : section));
  }, []);
  const [stumm, setStumm] = useState(false);
  const soundRef = useRef<Sound | null>(null);
  /** Welle, die gerade zum Server unterwegs ist - wird nach Bestaetigung geleert. */
  const sentDraft = useRef<WaveDraft | null>(null);
  const lastHandledWaveId = useRef<string | null>(null);

  // --- Enemy View (Abschnitt 83) ---
  // Als Zustand fuer die Oberflaeche UND als Ref fuer die Eingabe: Die
  // Bewegungsabfrage laeuft in jedem Bild und soll nicht neu erstellt werden.
  const [enemyView, setEnemyView] = useState(false);
  const enemyViewRef = useRef(false);

  /** Ist das Match vorbei? Dann nimmt die Spielflaeche keine Eingaben mehr an. */
  const matchOverRef = useRef(false);

  /*
   * Toene (Abschnitt 40).
   *
   * Browser lassen Ton erst nach einer Eingabe zu - deshalb wird der
   * Tonapparat beim ersten Antippen oder Tastendruck gestartet.
   */
  /*
   * Der zweite Finger (im Spiel aufgefallen am 27.09.2026): Wer mit dem linken
   * Daumen laeuft, bekommt vom Browser fuer den rechten Finger gar kein click.
   * Diese Regel reicht es nach - fuer JEDEN Knopf, auch fuer die, die erst
   * spaeter dazukommen. Die Begruendung steht in input/secondFinger.ts.
   */
  useEffect(() => installiereZweitenFinger(), []);

  /*
   * Vollbild schon im Menue (Rueckmeldung vom 15.10.2026).
   *
   * Bis dahin kam es erst beim Matchstart - man sass also im Menue mit
   * Adressleiste und Gestenleiste und bekam den Platz erst, wenn es
   * losging.
   *
   * Beim Oeffnen der Seite laesst es sich nicht anfragen: Browser
   * erlauben Vollbild ausschliesslich aus einer Eingabe heraus. Der
   * fruehestmoegliche Augenblick ist die erste Beruehrung - und die
   * passiert im Menue ohnehin sofort.
   *
   * ES MUSS DER KLICK SEIN. Bei Beruehrungen zaehlt als Eingabe erst das
   * Loslassen des Fingers, nicht das Aufsetzen - mit "pointerdown"
   * passierte am Handy gar nichts (Rueckmeldung vom 15.10.2026). Am
   * Rechner haette beides funktioniert.
   */
  useEffect(() => {
    let geklappt = false;

    const beimErstenTipp = () => {
      if (
        !vollbildBeimErstenTipp({
          // Auf dem iPhone kann der Browser es nicht - dann gar nicht
          // erst fragen (15.10.2026). Dort hilft der Home-Bildschirm.
          moeglich: vollbildMoeglich(),
          tippflaeche: isTouchDevice(),
          schonImVollbild: isFullscreen(),
          schonGeklappt: geklappt,
        })
      ) {
        return;
      }

      /*
       * Gemerkt wird erst, wenn es WIRKLICH geklappt hat. Sonst
       * verbrauchte ein abgelehnter Versuch den einen erlaubten - und auf
       * einem Geraet, das Vollbild gar nicht kann, waere danach auch der
       * Knopf in den Einstellungen die einzige Hoffnung.
       */
      void enterFullscreen().then(() => {
        geklappt = isFullscreen();
      });
    };

    window.addEventListener('click', beimErstenTipp);
    return () => window.removeEventListener('click', beimErstenTipp);
  }, []);

  useEffect(() => {
    const sound = new Sound();
    soundRef.current = sound;

    const starten = () => sound.unlock();
    window.addEventListener('pointerdown', starten);
    window.addEventListener('keydown', starten);

    /*
     * Im Hintergrund bleibt es still (Rueckmeldung vom 26.09.2026): Ein
     * offener Tab mit laufendem Match toente weiter, waehrend man laengst
     * etwas anderes machte. Das Match laeuft auf dem Server weiter - hoeren
     * muss man es deshalb nicht.
     */
    const sichtbarkeit = () => sound.setVisible(document.visibilityState === 'visible');
    sichtbarkeit();
    document.addEventListener('visibilitychange', sichtbarkeit);

    return () => {
      window.removeEventListener('pointerdown', starten);
      window.removeEventListener('keydown', starten);
      document.removeEventListener('visibilitychange', sichtbarkeit);
      sound.destroy();
      soundRef.current = null;
    };
  }, []);

  /*
   * Was einen Ton ausloest.
   *
   * Der Client hat keine Ereignisliste vom Server - er vergleicht deshalb den
   * neuen Zustand mit dem vorigen. Das reicht voellig: Ein Treffer ist ein
   * gesunkener Lebensbalken, ein Aufstieg ein hoeheres Level.
   */
  /**
   * Der Stand des letzten Vergleichs - mehr merkt sich der Ton nicht.
   *
   * Hier stand frueher auch ein Zaehler fuer Angriffstoene. Er wurde nur
   * weitergereicht und von niemandem gelesen - eine Zahl, die aussah, als
   * taete sie etwas.
   */
  const vorigerZustand = useRef<TonGedaechtnis>({ level: 1, portalHp: 0, noticeId: 0 });

  useEffect(() => {
    const sound = soundRef.current;
    if (!snapshot || !sound) return;

    /*
     * WAS zu hoeren ist, entscheidet matchToene - eine eigene Regel, weil
     * genau hier ein Fehler sass (15.10.2026): Beim Verlassen eines Matches
     * faellt der Schnappschuss auf den leeren zurueck, und der Vergleich
     * sah darin einen Treffer am Portal. Im Menue toente es also, als liefe
     * ein Creep ins Anchor.
     */
    const { toene, gedaechtnis } = matchToene(vorigerZustand.current, {
      level: snapshot.level,
      portalHp: snapshot.portalHp,
      notice: snapshot.notice,
      sichtbar: weltSichtbar(snapshot),
    });

    for (const ton of toene) sound.play(ton);
    vorigerZustand.current = gedaechtnis;
  }, [snapshot]);

  /*
   * Verbindung und Eingabe.
   *
   * Die Spielflaeche wird hier NICHT mehr gebraucht - die Welt entsteht seit
   * dem 13.10.2026 in einem eigenen Effekt. Die Pruefung bleibt trotzdem:
   * Sie haelt den Aufbau zurueck, solange die Infoseite zum geschlossenen
   * Playtest davorsteht, denn dann gibt es die Flaeche auch nicht.
   */
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    const inputController = new InputController();
    const skills = new SkillInput();
    setInput(inputController);
    setSkillInput(skills);
    skillInputRef.current = skills;

    const connection = new MatchConnection(resolveServerUrl());
    connectionRef.current = connection;

    /*
     * Griff fuer Entwicklung und Tests (Abschnitt 96): Nur so laesst sich ein
     * Verbindungsabbruch ueberhaupt nachstellen, ohne das WLAN auszuschalten.
     * Im ausgelieferten Spiel gibt es ihn nicht.
     */
    if (import.meta.env.DEV) {
      (window as unknown as Record<string, unknown>).__DUELINE_NET__ = connection;
    }

    // Die Verbindung holt sich die Bewegungsrichtung selbst ab - sie kennt
    // dabei nicht, ob sie von einer Tastatur, einem Joystick oder einem
    // Mausklick kommt (25.10.2026).
    //
    // In der Enemy View bleibt der eigene Hero ohne Eingabe stehen
    // (Abschnitt 83) - er greift aber weiter automatisch an.
    connection.setInputSource(() => {
      // In der Enemy View und waehrend einer Pause bleibt der Hero stehen.
      if (enemyViewRef.current || snapshotRef.current?.pausedFor) {
        mausBefehl.loesche();
        return { x: 0, y: 0 };
      }

      const gedrueckt = inputController.getMoveVector();
      /*
       * Eine Taste oder der Joystick gewinnt immer.
       *
       * In der Maussteuerung ist WASD aus, also kann hier nichts
       * ankommen - aber der Joystick kann es, wenn jemand am Laptop mit
       * Touchscreen spielt. Wer anfasst, befiehlt; der Klickbefehl ist
       * damit auch aufgehoben.
       */
      if (gedrueckt.x !== 0 || gedrueckt.y !== 0) {
        mausBefehl.loesche();
        return gedrueckt;
      }

      /*
       * Und sonst zaehlt der Klickbefehl. Die Regel steht in MausBefehl;
       * hier wird nur nachgesehen, wo alles gerade steht.
       */
      const player = connection.getOwnPlayer();
      if (!player || !player.alive) {
        mausBefehl.loesche();
        return { x: 0, y: 0 };
      }

      const state = connection.getState();
      const imSpiel = mausBefehl.richtung({
        eigene: connection.getRenderPosition(player),
        sucheEinheit: (id) => state?.creeps?.get(id) ?? state?.players?.get(id),
        reichweite:
          (getHero(player.heroId)?.attackRange ?? 0) * getHeroRangeFactor(player.talentId),
      });

      /*
       * Zurueck in die Form, die der InputController liefert (25.10.2026).
       *
       * Die Bewegung wird als BILDSCHIRMrichtung verschickt und unterwegs um
       * die Schraege der Kamera gedreht (18.10.2026). Ein angeklickter Punkt
       * liegt aber im Spiel - ohne diesen Rueckweg wuerde seine Richtung ein
       * zweites Mal gedreht, und der Hero liefe im Bogen zum Ziel.
       */
      return eingabeAusSpielrichtung(imSpiel, player.side as PlayerSide);
    });

    const unsubscribe = connection.subscribe((next) => {
      snapshotRef.current = next;
      setSnapshot(next);
    });
    snapshotRef.current = connection.getSnapshot();
    setSnapshot(connection.getSnapshot());

    /*
     * Kein automatischer Beitritt mehr: Zuerst kommt das Hauptmenue
     * (Abschnitt 88). Vorher landete man sofort in irgendeinem Match.
     *
     * Eine Ausnahme: Lief beim Neuladen gerade ein Match und der Platz ist
     * noch reserviert, geht es direkt dort weiter (Rueckmeldung vom
     * 25.09.2026 - nach einem Neuladen kam man sonst nicht mehr hinein).
     */
    void connection.resumeIfPossible();

    return () => {
      unsubscribe();
      inputController.destroy();
      void connection.disconnect();
      connectionRef.current = null;
      setInput(null);
      setSkillInput(null);
      skillInputRef.current = null;
      // Ein Befehl aus dem alten Match gilt im naechsten nicht mehr.
      mausBefehl.loesche();
    };
    /*
     * "tor" gehoert in die Liste, weil die Spielflaeche erst entsteht,
     * wenn die Infoseite zum geschlossenen Playtest weg ist. Ohne das lief
     * dieser Aufbau einmal ins Leere und nie wieder - die Seite blieb nach
     * richtigem Code leer (im Spiel aufgefallen am 04.10.2026).
     *
     * "mausBefehl" steht nur der Vollstaendigkeit halber in der Liste: Das
     * Objekt wechselt nie.
     */
  }, [tor, mausBefehl]);

  /*
   * Die Spielwelt entsteht ERST, wenn gleich gespielt wird
   * (Produktentscheidung vom 13.10.2026).
   *
   * Vorher stand sie im Effekt oben, zusammen mit Verbindung und Eingabe -
   * und wurde damit beim Oeffnen der Seite gebaut. Im Menue sah man nichts
   * davon; auf dem Handy kostete es trotzdem Ladezeit und Akku.
   *
   * WANN genau, entscheidet brauchtSpielwelt - eine eigene Regel, die ohne
   * Bild pruefbar ist. Kurz: ab dem 3-2-1 und bis man das Match verlaesst.
   *
   * Beim Verlassen wird sie abgeraeumt. Das ist der eigentliche Gewinn auf
   * dem Handy: Wer ins Menue zurueckgeht, haelt keine Welt mehr im Speicher.
   */
  const weltNoetig = snapshot ? brauchtSpielwelt(snapshot) : false;
  /*
   * Ist von der Welt gerade etwas zu sehen - Lane UND Oberflaeche?
   *
   * Rueckmeldung vom 13.10.2026: "man sieht schon Faehigkeiten, Traenke,
   * den Lebensbalken des Anchors usw. im Hintergrund - das soll nicht
   * sein", erst im Warteraum und dann auch noch blass hinter dem
   * Hauptmenue. Warteraum und Menue sind eigene Raeume, keine halben
   * Matches.
   *
   * Alles vom HUD haengt an dieser einen Frage. Wer etwas hinzufuegt,
   * nimmt sie mit - sonst scheint es wieder irgendwo durch.
   */
  const imBild = snapshot ? weltSichtbar(snapshot) : false;

  useEffect(() => {
    const stage = stageRef.current;
    const connection = connectionRef.current;
    const skills = skillInputRef.current;
    if (!weltNoetig || !stage || !connection || !skills) return;

    /*
     * Das Nachladen dauert einen Augenblick, und in dieser Zeit kann das
     * Match schon wieder vorbei sein - etwa, wenn der Gegner im Countdown
     * die Verbindung verliert. "abgebrochen" verhindert, dass dann noch eine
     * Welt entsteht, die niemand mehr abraeumt.
     */
    let abgebrochen = false;
    let aufraeumen: (() => void) | null = null;

    void import('./game/GameView.js').then(({ GameView }) => {
      if (abgebrochen) return;
      aufraeumen = baueWelt(GameView, stage, connection, skills, viewRef);
    });

    return () => {
      abgebrochen = true;
      aufraeumen?.();
    };
  }, [weltNoetig]);
  /** Loest einen Skill aus - egal ob per Taste, Schaltflaeche oder Zielstick. */
  const castSkill = useCallback(
    (
      slot: number,
      screenX: number | null,
      screenY: number | null,
      stick?: { deltaX: number; deltaY: number },
    ) => {
      // Waehrend einer Pause geht gar nichts (Abschnitt 30).
      if (snapshotRef.current?.pausedFor) return;

      const connection = connectionRef.current;
      const view = viewRef.current;
      const player = connection?.getOwnPlayer();
      if (!connection || !view || !player) return;

      /*
       * Solange WATCH FRONT gehalten wird, wirkt kein Skill (Abschnitt 13).
       *
       * Der Grund steht im Zielen: Ein Tipp auf den Bildschirm wird in
       * einen Punkt am Boden umgerechnet - und der laege bei dieser Kamera
       * an der Front statt beim Hero. Der Skill ginge also irgendwohin.
       */
      if (enemyViewRef.current) return;

      const definition = mitReichweite(getSkillInSlot(player.heroId, slot), player.talentId);
      if (!definition) return;

      /*
       * Der Zielstick rechnet aus der Auslenkung, alles andere aus dem Punkt
       * auf dem Boden. Beides landet beim selben Server - er prueft ohnehin
       * nach, ob der Punkt in Reichweite liegt (Abschnitt 28).
       */
      const ziel = stick
        ? resolveStickTarget(
            { x: player.x, y: player.y },
            player.facing,
            stick.deltaX,
            stick.deltaY,
            Math.max(definition.castRange, definition.dashDistance),
            player.side as PlayerSide,
          )
        : resolveAimTarget(
            { x: player.x, y: player.y },
            screenX !== null && screenY !== null ? view.screenToGround(screenX, screenY) : null,
            player.facing,
            definition.castRange,
          );

      connection.castSkill(slot, ziel);
      // Der Ton kommt sofort - ob der Skill wirklich zustande kommt, entscheidet
      // der Server. Ein spaeterer Ton faende sich gefuehlt "zu spaet" an.
      soundRef.current?.play('cast');
    },
    [],
  );

  /**
   * Muss fuer diesen Slot gezielt werden?
   *
   * Haengt am Hero: Derselbe Slot ist beim einen ein Flaechenzauber und beim
   * anderen ein Selbstbuff.
   */
  const brauchtZiel = useCallback((slot: number) => {
    const player = connectionRef.current?.getOwnPlayer();
    const definition = player ? getSkillInSlot(player.heroId, slot) : undefined;
    return definition ? needsAim(definition) : true;
  }, []);

  /*
   * Skills per Tastatur (Desktop) - Produktentscheidung vom 24.09.2026.
   *
   * Eine Zifferntaste WIRKT den Skill nicht mehr sofort, sondern nimmt ihn in
   * die Hand: Die Vorschau folgt der Maus, der naechste Linksklick loest aus.
   * Dieselbe Taste noch einmal oder Escape legt ihn wieder weg.
   */
  useEffect(() => {
    /*
     * Seit wann diese Taste gehalten wird (25.10.2026).
     *
     * Eine gewoehnliche Variable und kein Ref: Beide Zuhoerer entstehen
     * hier, leben gemeinsam und verschwinden gemeinsam.
     */
    let gehaltenSeit: { slot: number; zeit: number } | null = null;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Im Eingabefeld sind alle Kuerzel aus (25.09.2026).
      if (!shouldHandleShortcut(event)) return;

      if (event.code === 'Escape') {
        skillInput?.disarm();
        gehaltenSeit = null;
        return;
      }

      const slot = SKILL_TASTEN[steuerung][event.code];
      if (!slot || !skillInput || snapshotRef.current?.pausedFor) return;
      event.preventDefault();

      /*
       * Eine gehaltene Taste WIEDERHOLT sich (Robin, 25.10.2026: "dann
       * flimmert der Kreis").
       *
       * Und "arm" ist ein Umschalter: Jede Wiederholung legte den Skill
       * abwechselnd hin und weg - dreissig mal je Sekunde. Gezaehlt wird
       * nur der ERSTE Druck.
       */
      if (event.repeat) return;

      /*
       * Solange WATCH FRONT gehalten wird, nimmt auch die Zifferntaste
       * keinen Skill in die Hand (Abschnitt 13).
       *
       * Hier und nicht erst in castSkill: Eine Zifferntaste wirkt den
       * Skill nicht, sie armiert ihn. Ohne diese Stelle haette der Spieler
       * die Front vor sich und eine Zielvorschau, die am Hero haengt -
       * also ausserhalb des Bildes.
       */
      if (enemyViewRef.current) return;

      /*
       * Skills, die nur am Hero selbst passieren, kommen sofort - ohne
       * Vorschau und ohne zweiten Klick (Rueckmeldung vom 25.09.2026).
       */
      if (!brauchtZiel(slot)) {
        skillInput.disarm();
        castSkill(slot, null, null);
        return;
      }

      gehaltenSeit = { slot, zeit: performance.now() };
      skillInput.arm(slot, lastPointer.current.x, lastPointer.current.y);
    };

    /**
     * Loslassen wirkt - wenn die Taste gehalten wurde
     * (Produktentscheidung vom 25.10.2026).
     *
     * Damit gibt es die beiden Wege, die Robin wollte:
     *
     *   - TIPPEN: Der Skill bleibt in der Hand, man zielt in Ruhe und loest
     *     mit dem Linksklick aus.
     *   - HALTEN: Man zielt waehrenddessen mit der Maus, und das Loslassen
     *     loest aus.
     *
     * Hat der Spieler zwischendurch etwas anderes in die Hand genommen oder
     * den Skill schon gewirkt, passiert hier nichts mehr.
     */
    const handleKeyUp = (event: KeyboardEvent) => {
      const start = gehaltenSeit;
      gehaltenSeit = null;
      if (!start || !skillInput) return;
      if (SKILL_TASTEN[steuerung][event.code] !== start.slot) return;

      // Nur getippt? Dann bleibt er liegen und wartet auf den Linksklick.
      if (!tasteGehalten(performance.now() - start.zeit)) return;

      const bereit = skillInput.getArmed();
      if (!bereit || bereit.slot !== start.slot) return;

      const { x, y } = lastPointer.current;
      skillInput.disarm();
      castSkill(start.slot, x, y);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [skillInput, brauchtZiel, castSkill, steuerung]);

  /*
   * Das angeklickte Ziel der Maussteuerung (25.10.2026).
   *
   * Entweder ein Punkt am Boden oder eine Einheit. Der Unterschied ist
   * nicht nur Buchhaltung: Einem Punkt laeuft man einmal entgegen und
   * bleibt dort stehen, einer Einheit laeuft man nach, bis sie in
   * Reichweite ist - und sie bewegt sich dabei.
   *
   * Es steht in einem Ref und nicht im Zustand: Die Richtung wird in jedem
   * Takt abgefragt, und React soll dafuer nicht neu zeichnen.
   */

  // Letzte bekannte Mausposition - sie dient am Desktop als Zielpunkt.
  const lastPointer = useRef<{ x: number | null; y: number | null }>({ x: null, y: null });
  useEffect(() => {
    const handleMove = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse') return;
      lastPointer.current = { x: event.clientX, y: event.clientY };
      skillInputRef.current?.moveArmed(event.clientX, event.clientY);
    };
    window.addEventListener('pointermove', handleMove);
    return () => window.removeEventListener('pointermove', handleMove);
  }, []);

  // --- Army Composer: Welle bearbeiten und senden ---
  // Funktionale Updates: Beim Halten einer Karte wird sehr schnell hintereinander
  // hinzugefuegt - jeder Schritt muss auf dem neuesten Stand aufbauen.
  /**
   * Fuegt einen Creep in die Welle - wenn er hineindarf.
   *
   * Die Pruefung steht bewusst HIER und nicht nur an der Schaltflaeche: Eine
   * ausgegraute Karte ist Anzeige, keine Regel. Im Spiel lief eine laufende
   * Wiederholung sonst ueber die Goldgrenze hinaus weiter (24.09.2026).
   */
  const addToDraft = useCallback((creepId: string) => {
    const spieler = snapshotRef.current;
    const creep = getCreep(creepId);

    setDraft((vorher) => {
      if (!creep || !spieler) return vorher;
      const pruefung = canAddCreep(creep, vorher, spieler, spieler.enemyLaneCreeps);
      return pruefung.allowed ? addCreep(vorher, creepId) : vorher;
    });
  }, []);

  const removeFromDraft = useCallback((creepId: string) => {
    setDraft((vorher) => removeCreep(vorher, creepId));
  }, []);

  const clearDraft = useCallback(() => setDraft(emptyDraft()), []);

  const loadIntoDraft = useCallback((wave: WaveDraft) => setDraft(copyDraft(wave)), []);

  /** SEND. Bezahlt wird erst, wenn der Server annimmt (Abschnitt 70). */
  const sendDraft = useCallback(() => {
    sentDraft.current = copyDraft(draft);
    connectionRef.current?.sendWave(copyDraft(draft));
  }, [draft]);

  /** LAST WAVE kurz getippt: erneut senden (Abschnitt 74). */
  const resendLastWave = useCallback(() => {
    const last = snapshot?.waveHistory[0];
    if (last) connectionRef.current?.sendWave(copyDraft(last));
  }, [snapshot]);

  /** LAST WAVE lange gedrueckt: in den Composer laden und bearbeiten (Abschnitt 74). */
  const editLastWave = useCallback(() => {
    const last = snapshot?.waveHistory[0];
    if (!last) return;
    setDraft(copyDraft(last));
    setActiveSection('army');
  }, [snapshot]);

  // Hat der Server genau die Welle aus dem Composer angenommen, wird der
  // Composer geleert - fertig fuer die naechste. Wurde sie abgelehnt, bleibt sie
  // stehen, damit man sie anpassen kann.
  const lastSentWaveId = snapshot?.lastSentWaveId ?? null;
  const lastSentWave = snapshot?.waveHistory[0] ?? null;
  useEffect(() => {
    if (!lastSentWaveId || lastSentWaveId === lastHandledWaveId.current) return;
    lastHandledWaveId.current = lastSentWaveId;
    const gesendet = sentDraft.current;
    sentDraft.current = null;
    if (gesendet && lastSentWave && isSameDraft(gesendet, lastSentWave)) {
      setDraft((aktuell) => (isSameDraft(aktuell, gesendet) ? emptyDraft() : aktuell));
    }
  }, [lastSentWaveId, lastSentWave]);

  // --- Enemy View umschalten ---
  const changeEnemyView = useCallback((an: boolean) => {
    // Ohne Gegner gibt es keine Enemy Line zu sehen.
    const ziel = an && connectionRef.current?.getOpponentSessionId() ? true : false;
    enemyViewRef.current = ziel;
    setEnemyView(ziel);
    viewRef.current?.setViewMode(ziel ? 'frontline' : 'own');
  }, []);

  /*
   * Watch Frontline ist ein Haltegriff (Rueckmeldung vom 09.10.2026).
   *
   * Vorher war es ein Umschalter, und die Kamera kam zusaetzlich bei
   * jeder Bewegung und bei jedem Skill von selbst zurueck. Beides ist
   * weg: Der Blick dauert genau so lange, wie der Finger liegt.
   */
  const watchStart = useCallback(() => changeEnemyView(true), [changeEnemyView]);
  const watchEnd = useCallback(() => changeEnemyView(false), [changeEnemyView]);

  // Nach dem Matchende nimmt die Spielflaeche keine Eingaben mehr an.
  const matchOver = snapshot?.outcome != null;
  useEffect(() => {
    matchOverRef.current = matchOver;
  }, [matchOver]);

  // Geht der Gegner, gibt es nichts mehr zu beobachten.
  const hatGegner = snapshot?.opponent !== null && snapshot?.opponent !== undefined;
  useEffect(() => {
    if (!hatGegner && enemyViewRef.current) changeEnemyView(false);
  }, [hatGegner, changeEnemyView]);

  // Desktop: B oeffnet und schliesst den Composer, V wechselt die Lane,
  // Escape schliesst den Composer.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!shouldHandleShortcut(event)) return;
      // Waehrend einer Pause laesst sich nicht einmal ein Fenster oeffnen.
      if (snapshotRef.current?.pausedFor) return;

      if (event.code === 'KeyB') toggleSection('army');
      if (event.code === 'KeyK') toggleSection('talent');
      if (event.code === 'KeyO') toggleSection('shop');
      // Trinken: F fuer Leben, G fuer Mana.
      if (event.code === 'KeyF') connectionRef.current?.useConsumable('health_potion');
      if (event.code === 'KeyG') connectionRef.current?.useConsumable('mana_potion');
      /*
       * V wird GEHALTEN, nicht umgeschaltet - wie der Knopf. Die
       * Wiederholung der Tastatur wird uebergangen, sonst liefe
       * changeEnemyView zehnmal in der Sekunde.
       */
      if (event.code === 'KeyV' && !event.repeat) watchStart();
      // Escape schliesst, was gerade offen ist.
      if (event.code === 'Escape') setActiveSection(null);
    };
    // Loslassen beendet den Frontblick - auch wenn das Fenster den Fokus
    // verliert, sonst bliebe die Kamera haengen.
    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.code === 'KeyV') watchEnd();
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', watchEnd);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', watchEnd);
    };
  }, [watchStart, watchEnd, toggleSection]);

  /** Schaltet den Ton stumm oder wieder an. */
  const toggleSound = useCallback(() => {
    const sound = soundRef.current;
    if (!sound) return;
    sound.unlock();
    setStumm(sound.toggleMute());
  }, []);

  const handleDevAction = useCallback((action: DevActionTypeValue) => {
    connectionRef.current?.devAction(action);
  }, []);

  const handleRetry = useCallback(() => {
    void connectionRef.current?.connect();
  }, []);

  /** Aus dem Hauptmenue heraus ein Match beginnen (Abschnitt 88). */
  const startMatch = useCallback((mode: JoinMode, code: string, heroId: string, botHeroId = '') => {
    /*
     * Am Handy geht es beim Matchstart ins Vollbild (Rueckmeldung vom
     * 26.09.2026): Die Adressleiste des Browsers nimmt oben rund ein Viertel
     * der Hoehe weg, und die Gestenleiste von Android liegt im Querformat auf
     * dem rechten Rand - genau dort, wo die Bedienung sitzt.
     *
     * SEIT DEM 15.10.2026 ist das meist schon erledigt: Vollbild wird beim
     * ersten Antippen im Menue angefragt. Der Aufruf bleibt trotzdem stehen -
     * er greift, wenn jemand es zwischendurch verlassen hat, und kostet
     * nichts, wenn es ohnehin schon an ist.
     */
    if (isTouchDevice()) void enterFullscreen();

    void connectionRef.current?.connect(mode, code, heroId, botHeroId);
  }, []);

  /** Zurueck ins Hauptmenue - ohne die Seite neu zu laden. */
  const leaveMatch = useCallback(() => {
    setActiveSection(null);
    setEnemyView(false);
    void connectionRef.current?.leaveMatch();
  }, []);

  /**
   * Tippen auf die Spielflaeche: Gegner auswaehlen oder Auswahl aufheben.
   *
   * Wird von zwei Stellen gerufen - direkt von der Spielflaeche und vom
   * Joystick, wenn dessen Beruehrung nur ein kurzes Antippen war.
   */
  const selectTargetAt = useCallback(
    (screenX: number, screenY: number) => {
      const view = viewRef.current;
      const connection = connectionRef.current;
      if (!view || !connection) return;

      // Liegt ein Skill bereit, gilt auch ein Tipp in der linken Haelfte ihm.
      const skills = skillInputRef.current;
      const bereit = skills?.release(screenX, screenY);
      if (bereit) {
        castSkill(bereit.slot, screenX, screenY);
        return;
      }
      // In der Enemy View wird nichts ausgewaehlt - und die eigene Auswahl bleibt.
      if (enemyViewRef.current) return;
      // Nach dem Matchende gibt es nichts mehr auszuwaehlen.
      if (matchOverRef.current) return;

      // Das kann ein Creep sein oder der gegnerische Hero.
      const zielId = view.findZielNearScreen(screenX, screenY);
      connection.selectTarget(zielId);
      view.setSelectedCreep(zielId);
    },
    [castSkill],
  );

  /**
   * Ein Laufbefehl mit der Maus (Produktentscheidung vom 25.10.2026).
   *
   * Zwei Faelle, und sie unterscheiden sich in einem Punkt: Liegt ein
   * Gegner unter dem Zeiger, wird er zugleich ZIEL - dann laeuft der Hero
   * ihm nach und schlaegt zu, sobald er ihn erreicht. Sonst ist es ein
   * Punkt am Boden.
   *
   * Welche Einheit unter dem Zeiger liegt, weiss das 3D-Bild; es liefert
   * ausdruecklich nur Gegner (findZielNearScreen). Eigene Einheiten kann
   * man also nicht versehentlich angreifen wollen.
   */
  const laufBefehl = useCallback(
    (screenX: number, screenY: number) => {
      const view = viewRef.current;
      const connection = connectionRef.current;
      if (!view || !connection) return;

      // In der Enemy View und nach dem Matchende gibt es nichts zu befehlen.
      if (enemyViewRef.current || matchOverRef.current) return;

      const zielId = view.findZielNearScreen(screenX, screenY);
      if (zielId) {
        mausBefehl.setzeEinheit(zielId);
        // Dasselbe Ziel wie beim Antippen - der Server greift es dann an.
        connection.selectTarget(zielId);
        view.setSelectedCreep(zielId);
        // Sichtbar machen, dass der Klick ankam (Optik, GameView).
        view.zeigeKlickMarke({ zielId });
        return;
      }

      const boden = view.screenToGround(screenX, screenY);
      // Oberhalb des Horizonts gibt es keinen Punkt - dann eben kein Befehl.
      if (boden) {
        mausBefehl.setzePunkt(boden);
        view.zeigeKlickMarke({ punkt: boden });
      }
    },
    [mausBefehl],
  );

  /**
   * Klick auf die Spielflaeche.
   *
   * Liegt ein Skill in der Hand, gilt der Klick ihm - und nur ihm: Sonst
   * waehlte derselbe Klick nebenbei noch einen Gegner aus. Ein Rechtsklick
   * legt den Skill wieder weg.
   */
  const handleStagePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const skills = skillInputRef.current;

      if (skills?.getArmed()) {
        if (event.button === 2) {
          skills.disarm();
          return;
        }
        const bereit = skills.release(event.clientX, event.clientY);
        if (bereit) castSkill(bereit.slot, event.clientX, event.clientY);
        return;
      }

      /*
       * Rechtsklick laeuft - aber nur in der Maussteuerung
       * (Produktentscheidung vom 25.10.2026).
       *
       * Liegt ein Skill bereit, kommt diese Stelle gar nicht dran: Dort
       * legt der Rechtsklick ihn weg, wie bisher. Das ist Absicht - die
       * Geste zum Abbrechen ist aelter und sitzt im Finger.
       */
      if (steuerung === 'maus' && event.button === 2) {
        laufBefehl(event.clientX, event.clientY);
        return;
      }

      selectTargetAt(event.clientX, event.clientY);
    },
    [selectTargetAt, castSkill, laufBefehl, steuerung],
  );

  /*
   * Die Tuer steht ganz vorn (Produktentscheidung vom 04.10.2026).
   *
   * Bewusst VOR der Spielflaeche: Wer nicht hereindarf, soll nicht erst
   * das 3D-Bild und das Menue geladen bekommen. Alle Hooks sind an dieser
   * Stelle schon gelaufen, der fruehe Ausstieg ist also unbedenklich.
   */
  if (tor === 'frage') return <div className="dl-app" />;
  if (tor === 'zu') {
    return (
      <div className="dl-app">
        <Zugangstor onOffen={() => setTor('offen')} />
      </div>
    );
  }

  return (
    <div className="dl-app">
      {/*
        Der Weg zum Vollbild auf dem iPhone - ueber allem, bevor man im
        Menue etwas anfangen kann (Robin, 15.10.2026). Er zeigt sich nur
        dort, wo Vollbild wirklich nicht geht; die Regel steht in
        startbildschirm.ts.
      */}
      <StartbildschirmHinweis />

      {/* Das 3D-Bild liegt ganz unten, die Oberflaeche darueber.
          Die Zeichenflaeche selbst erzeugt und entfernt GameView. */}
      {/*
        Verborgen, solange der Warteraum laeuft: Die Welt laedt dort schon,
        sehen soll man sie aber erst beim Start (13.10.2026).
      */}
      <div
        ref={stageRef}
        className={'dl-stage' + (imBild ? '' : ' dl-stage--verborgen')}
        onPointerDown={handleStagePointerDown}
        // Ohne das oeffnete der Rechtsklick das Kontextmenue des Browsers,
        // statt einen bereitliegenden Skill wegzulegen.
        onContextMenu={(event) => event.preventDefault()}
      />

      <OrientationGate>
        {snapshot && (
          <>
            {/* Der Joystick liegt unter dem restlichen HUD, damit Schaltflaechen
                immer Vorrang vor der Bewegungssteuerung haben. */}
            {/* Kein Joystick in der Enemy View und nicht mehr nach dem
                Matchende - sonst liegt er ueber der Ergebnisanzeige. */}
            {/*
              UND KEINER IN DER MAUSSTEUERUNG (Robin, 25.10.2026: "wenn ich
              die Maustaste laenger druecke, kommt der Joystick - das macht
              keinen Sinn").

              Er nimmt jeden Zeiger an, auch die Maus, und er liegt UEBER
              der Spielflaeche. Damit hat er die Rechtsklicks geschluckt,
              bevor der Laufbefehl sie sehen konnte - deshalb lief in der
              Maussteuerung gar nichts.
            */}
            {input && imBild && !enemyView && !snapshot.outcome && steuerung !== 'maus' && (
              <VirtualJoystick input={input} onTap={selectTargetAt} />
            )}

            {imBild && (
              <HudShell
                snapshot={snapshot}
                activeSection={activeSection}
                onOpenArmy={() => toggleSection('army')}
                onOpenShop={() => toggleSection('shop')}
                stumm={stumm}
                onToggleSound={toggleSound}
                onOpenSkillTree={() => toggleSection('talent')}
                onResendLastWave={resendLastWave}
                onEditLastWave={editLastWave}
                enemyView={enemyView}
                onWatchStart={watchStart}
                onWatchEnd={watchEnd}
                onSurrender={() => connectionRef.current?.surrender()}
              />
            )}

            {skillInput && imBild && !enemyView && !snapshot.outcome && (
              <SkillBar
                snapshot={snapshot}
                skillInput={skillInput}
                onCast={castSkill}
                steuerung={steuerung}
              />
            )}

            {/* Traenke: direkt ueber der Skill-Leiste, damit sie auf dem Handy
                mit demselben Daumen erreichbar sind. */}
            {imBild && !enemyView && !snapshot.outcome && (
              <PotionBar
                snapshot={snapshot}
                onUse={(itemId) => connectionRef.current?.useConsumable(itemId)}
              />
            )}

            {enemyView && snapshot.status === 'connected' && (
              <WatchFrontlineBanner snapshot={snapshot} />
            )}

            {shopOpen && snapshot.status === 'connected' && !snapshot.outcome && (
              <ItemShop
                snapshot={snapshot}
                onBuy={(itemId) => connectionRef.current?.buyItem(itemId)}
                onSell={(itemId) => connectionRef.current?.sellItem(itemId)}
                onBuyConsumable={(itemId) => connectionRef.current?.buyConsumable(itemId)}
                onClose={() => setActiveSection(null)}
              />
            )}

            {skillTreeOpen && snapshot.status === 'connected' && !snapshot.outcome && (
              <SkillTreePanel
                snapshot={snapshot}
                onSpend={(slot) => connectionRef.current?.spendSkillPoint(slot)}
                onChooseTalent={(choiceId) => connectionRef.current?.chooseTalent(choiceId)}
                onChooseCapstone={(choiceId) => connectionRef.current?.chooseCapstone(choiceId)}
                onClose={() => setActiveSection(null)}
              />
            )}

            {composerOpen && snapshot.status === 'connected' && (
              <ArmyComposer
                snapshot={snapshot}
                draft={draft}
                onAdd={addToDraft}
                onRemove={removeFromDraft}
                onClear={clearDraft}
                onLoad={loadIntoDraft}
                onSend={sendDraft}
                onClose={() => setActiveSection(null)}
                onUpgradeTier={() => connectionRef.current?.upgradeTier()}
              />
            )}

            {/*
              Auch das Entwicklerfeld gehoert ins Match und nicht ins Menue
              (13.10.2026): Seine Werte - Bildrate, Ping, Servertakt - gibt es
              ohne Match gar nicht, und seine Knoepfe wirken dort nicht.
            */}
            {imBild && (
              <div className="dl-debug-anchor">
                <DebugPanel snapshot={snapshot} onDevAction={handleDevAction} />
              </div>
            )}
            {/*
              Nur, wenn eine Verbindung wirklich weggebrochen ist
              (13.10.2026). Im Menue sagt das Menue selbst Bescheid - sonst
              stand dort "Connection lost" blass hinter dem Kasten.
            */}
            {zeigeVerbindungsmeldung(snapshot) && (
              <ConnectionOverlay snapshot={snapshot} onRetry={handleRetry} />
            )}

            {/* Pause, weil jemand weg ist (Abschnitt 30). */}
            <PauseNotice snapshot={snapshot} />
            <MatchEndOverlay
              snapshot={snapshot}
              onToggleReady={() => connectionRef.current?.toggleLobbyReady()}
              onSignal={(signal) => connectionRef.current?.sendLobbySignal(signal)}
              onLeave={leaveMatch}
            />

            {/* Solange man in keinem Match ist, liegt das Hauptmenue darueber. */}
            {(snapshot.status === 'idle' ||
              snapshot.status === 'connecting' ||
              snapshot.status === 'error') && (
              <MainMenu
                snapshot={snapshot}
                onStart={startMatch}
                stumm={stumm}
                onToggleSound={toggleSound}
                steuerung={steuerung}
                onSteuerung={waehleSteuerung}
              />
            )}

            {/*
              Der Raum vor dem Match (Produktentscheidung vom 05.10.2026).

              Er erscheint SOFORT beim Start, nicht erst wenn der Gegner da
              ist: Vorher sass man zuerst auf der leeren Lane und wurde dann
              in einen anderen Bildschirm geschoben. Jetzt ist es ein Raum,
              in dem der Gegner erscheint.

              Er liegt halbdurchsichtig ueber der Lane - man sieht schon,
              wo man gleich spielt.
            */}
            {snapshot.status === 'connected' &&
              (snapshot.phase === 'waiting' ||
                snapshot.phase === 'lobby' ||
                snapshot.phase === 'starting') &&
              !snapshot.outcome && (
                <Lobby
                  snapshot={snapshot}
                  onToggleReady={() => connectionRef.current?.toggleLobbyReady()}
                  onSignal={(signal) => connectionRef.current?.sendLobbySignal(signal)}
                  onSelectHero={(heroId) => connectionRef.current?.selectHero(heroId)}
                  onLeave={leaveMatch}
                />
              )}
          </>
        )}
      </OrientationGate>
    </div>
  );
}
